
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import LoginForm from '@/components/auth/LoginForm';
import { Loader2 } from 'lucide-react';
import Logo from '@/components/navbar/Logo';

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [pageLoading, setPageLoading] = useState(true);

  // If user is already logged in, redirect to home page
  useEffect(() => {
    if (!loading) {
      if (user) {
        // User is logged in, redirect to home
        console.log("User is already logged in, redirecting to home");
        navigate('/');
      } else {
        // User is not logged in, show the login page
        console.log("User is not logged in, showing login page");
      }
      // Set pageLoading to false once auth state is determined
      setPageLoading(false);
    }
  }, [user, loading, navigate]);

  // Show loading state during auth check
  if (loading || pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-center flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" id="login-top">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="inline-flex items-center justify-center">
            <div className="h-12 w-40 relative flex justify-center">
              <Logo textSize="text-2xl" linkTo={false} />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Sign in to your account</h2>
          <p className="mt-2 text-sm text-gray-600">
            Don't have an account?{" "}
            <Link to="/signup" className="font-medium text-purple-600 hover:text-purple-500">
              Sign up
            </Link>
          </p>
        </div>

        <Card className="shadow-lg border-gray-200">
          <CardHeader>
            <CardTitle className="text-xl">Login</CardTitle>
            <CardDescription>Enter your email and password to sign in</CardDescription>
          </CardHeader>
          <CardContent>
            <LoginForm />
          </CardContent>
          <CardFooter className="flex justify-center">
            <p className="text-xs text-gray-500">
              By signing in, you agree to our <Link to="/terms" className="text-purple-600 hover:text-purple-500">Terms of Service</Link> and <Link to="/privacy" className="text-purple-600 hover:text-purple-500">Privacy Policy</Link>.
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
